/**
 * Relation quick-add helpers for picker modals.
 */

import { normalizeSchemaFieldType } from 'utilities/FieldTypes';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { coerceFromInput } from 'utilities/NodeFieldCoercion';

/**
 * findOptionByExactTitle
 *
 * Finds a non-empty option whose label matches title (trim + case-insensitive).
 *
 * @param {Array<{ value?: string, label?: string }>} options
 * @param {string} title
 * @returns {object|null}
 */
export function findOptionByExactTitle(options, title) {
  const needle = String(title || '').trim().toLowerCase();
  if (!needle) return null;

  const list = Array.isArray(options) ? options : [];
  for (const option of list) {
    const value = String(option?.value ?? '').trim();
    if (!value) continue;
    const label = String(option?.label || '').trim().toLowerCase();
    if (label === needle) return option;
  }

  return null;
}

/**
 * Whether a quick-add draft value should count as populated.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isRelationQuickAddValuePopulated(value) {
  if (value === undefined || value === null) return false;
  // Unchecked booleans are the empty default; only true counts as filled.
  if (typeof value === 'boolean') return value === true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.keys(value).some((key) => {
      const entry = value[key];
      if (entry === undefined || entry === null) return false;
      if (typeof entry === 'string') return entry.trim().length > 0;
      return true;
    });
  }
  return false;
}

/**
 * Builds an empty values map for quick-add display fields.
 *
 * @param {unknown} fieldDefs
 * @returns {Record<string, unknown>}
 */
export function buildEmptyRelationQuickAddValues(fieldDefs) {
  const next = {};
  (Array.isArray(fieldDefs) ? fieldDefs : []).forEach((field) => {
    const name = String(field?.name || '').trim();
    if (!name) return;
    const type = normalizeSchemaFieldType(field?.type);
    if (type === 'boolean') {
      next[name] = false;
      return;
    }
    if (type === 'location') {
      next[name] = null;
      return;
    }
    next[name] = '';
  });
  return next;
}

/**
 * Whether any quick-add display-field value is populated.
 *
 * @param {Record<string, unknown>|null|undefined} values
 * @returns {boolean}
 */
export function hasRelationQuickAddFieldValues(values) {
  if (!values || typeof values !== 'object') return false;
  return Object.values(values).some((entry) => isRelationQuickAddValuePopulated(entry));
}

/**
 * Applies quick-add display-field values onto a newly created node.
 *
 * @param {object} node
 * @param {unknown} fieldDefs
 * @param {Record<string, unknown>|null|undefined} values
 * @param {string} typeKey
 * @returns {object}
 */
export function applyRelationQuickAddValues(node, fieldDefs, values, typeKey) {
  let next = node && typeof node === 'object' ? { ...node } : {};
  const bag = values && typeof values === 'object' ? values : {};
  const resolvedTypeKey = String(typeKey || next?.type || '').trim();

  (Array.isArray(fieldDefs) ? fieldDefs : []).forEach((field) => {
    const name = String(field?.name || '').trim();
    if (!name || !Object.prototype.hasOwnProperty.call(bag, name)) return;

    const raw = bag[name];
    if (!isRelationQuickAddValuePopulated(raw)) return;

    const coerced = coerceFromInput(raw, normalizeSchemaFieldType(field?.type), field);
    if (!isRelationQuickAddValuePopulated(coerced)) return;

    const patch = buildNodeFieldPatch(next, field, resolvedTypeKey, coerced);
    if (patch) next = { ...next, ...patch };
  });

  return next;
}
